import { useCallback, useRef } from 'react';
import { MAX_PANEL_WIDTH, MIN_PANEL_WIDTH } from './usePanelWidth';

/** A scroller that stops at this track pads its side with `insetBesideResizeTrack`, never a
 * literal remainder, so a change here does not move its content off the inset. */
export const PANEL_RESIZE_TRACK_WIDTH = 8;

const KEYBOARD_STEP = 24;

interface Props {
  /**
   * Which side of the row the panel it resizes sits on, and so which way a drag widens it.
   *
   * Required rather than defaulted: the two directions are mirror images, and a handle wired to
   * the wrong one narrows the panel when you pull it open. That is a bug nobody reads in a diff,
   * so the caller is made to say which panel it has.
   */
  side: 'left' | 'right';
  /** Current width, used as the drag baseline. */
  width: number;
  /** Live update during drag (state only). */
  onResize: (next: number) => void;
  /** Commit the final width on release (persists). */
  onResizeEnd: (next: number) => void;
  label: string;
  dataTestId?: string;
}

/**
 * The handle occupies a `PANEL_RESIZE_TRACK_WIDTH` track so it recreates the
 * inter-card seam a row `gap` would otherwise supply — hosts drop their gap rather than double it.
 *
 * A drag is measured from the pointer's starting x against the width at that moment, then signed
 * by which side the panel is on: a right-hand panel grows as the pointer moves left, a left-hand
 * one as it moves right. Keyboard steps follow the same sign, so the arrow that widens by drag is
 * the arrow that widens by key.
 */
export function PanelResizeHandle({
  side,
  width,
  onResize,
  onResizeEnd,
  label,
  dataTestId,
}: Props) {
  const startX = useRef(0);
  const startWidth = useRef(width);
  const latest = useRef(width);
  /**
   * Whether a frame is already queued. A boolean rather than the frame id,
   * because the id is only assigned after `requestAnimationFrame` returns — so a
   * callback that runs before then would leave the guard set and swallow every
   * later move.
   */
  const isFrameQueued = useRef(false);

  // +1 when moving the pointer right widens the panel, -1 when it narrows it.
  const grain = side === 'left' ? 1 : -1;

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLElement>) => {
      e.preventDefault();
      startX.current = e.clientX;
      startWidth.current = width;
      latest.current = width;
      const prevUserSelect = document.body.style.userSelect;
      // eslint-disable-next-line i18next/no-literal-string -- CSS property value
      document.body.style.userSelect = 'none';

      /**
       * Coalesced to one update per frame. The width lives in the host page, so
       * every update re-renders the whole detail tree — preview, filmstrip and
       * every comment thread — which is several times more work than the screen
       * can show.
       */
      const onMove = (ev: PointerEvent) => {
        latest.current = startWidth.current + grain * (ev.clientX - startX.current);
        if (isFrameQueued.current) return;
        isFrameQueued.current = true;
        requestAnimationFrame(() => {
          if (!isFrameQueued.current) return;
          isFrameQueued.current = false;
          onResize(latest.current);
        });
      };
      const cleanup = () => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onEnd);
        window.removeEventListener('pointercancel', onEnd);
        document.body.style.userSelect = prevUserSelect;
      };
      const onEnd = () => {
        // No flush needed: `latest.current` tracks every raw move, so the
        // committed width is exact even when a queued frame never ran.
        isFrameQueued.current = false;
        cleanup();
        onResizeEnd(latest.current);
      };

      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onEnd);
      window.addEventListener('pointercancel', onEnd);
    },
    [width, onResize, onResizeEnd, grain],
  );

  return (
    // A button rather than a separator: it is operated, not just a divider, and the
    // slider role carries the width to assistive tech so it can be driven by keyboard.
    //
    // Horizontal, though the bar drawn inside it is vertical: for a slider `aria-orientation`
    // is the axis the VALUE moves along, and this one carries a width. Calling it vertical
    // would promise Up and Down keys, which this handle does not answer.
    <button
      type="button"
      role="slider"
      aria-orientation="horizontal"
      aria-label={label}
      aria-valuenow={width}
      aria-valuemin={MIN_PANEL_WIDTH}
      aria-valuemax={MAX_PANEL_WIDTH}
      data-testid={dataTestId}
      onPointerDown={onPointerDown}
      onKeyDown={(e) => {
        if (e.key === 'ArrowLeft') onResizeEnd(width - grain * KEYBOARD_STEP);
        else if (e.key === 'ArrowRight') onResizeEnd(width + grain * KEYBOARD_STEP);
        // Not signed by `grain`, unlike the arrows: ARIA puts Home at the first allowed value
        // and End at the last, so these are the bounds this element already publishes as
        // `aria-valuemin`/`aria-valuemax` rather than directions to move in.
        else if (e.key === 'Home') onResizeEnd(MIN_PANEL_WIDTH);
        else if (e.key === 'End') onResizeEnd(MAX_PANEL_WIDTH);
        else return;
        e.preventDefault();
      }}
      style={{ width: PANEL_RESIZE_TRACK_WIDTH }}
      className="group flex shrink-0 cursor-col-resize items-center justify-center"
    >
      <div className="h-8 w-0.5 rounded-full bg-border opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100" />
    </button>
  );
}
